import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { useSelector } from 'react-redux';

import GeneralSection from './components/general-section/GeneralSection';
import SocialSection from './components/SocialSection';
import ProjectSettingsSection from './components/ProjectSettingsSection';
import CommonButton from '../../../../components/common-button/CommonButton';

import scss from './SonarDatasetModal.module.scss';

export const ADD_DATASET_SECTIONS = {
  GENERAL: 'general',
  SOCIAL: 'social',
  PROJECT_SETTINGS: 'projectSettings:',
};

export const SONAR_DATASET_TABS = {
  GENERAL: 0,
  PROJECT_SETTINGS: 1,
  SOCIAL: 2,
};

const requiredFieldsForTabs = {
  [SONAR_DATASET_TABS.GENERAL]: [],
  [SONAR_DATASET_TABS.PROJECT_SETTINGS]: ['brandCompany', 'country', 'domain'],
  [SONAR_DATASET_TABS.SOCIAL]: ['brandCompany', 'domain', 'country', 'competitor'],
};

const fieldsByTabs = {
  [SONAR_DATASET_TABS.GENERAL]: ['country', 'brandCompany', 'domain', 'categories', 'products', 'services'],
  [SONAR_DATASET_TABS.PROJECT_SETTINGS]: ['competitor', 'inEcosystem', 'inFocus', 'groups'],
  [SONAR_DATASET_TABS.SOCIAL]: ['FACEBOOK', 'INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'LINKEDIN'],
};

const SonarDatasetModalForm = ({ onSubmit, onClose, isEdit }) => {
  const { t } = useTranslation('projects');
  const [selectedTab, setSelectedTab] = useState(SONAR_DATASET_TABS.GENERAL);

  const isEditableDatasetLoading = useSelector((state) => state.sonarManagement.isEditableDatasetLoading);

  const { restart, getFieldState } = useForm();
  const { valid, values, errors, submitting } = useFormState();

  const isLoading = submitting || isEditableDatasetLoading;

  const handleSubmitAndCLose = async () => {
    await onSubmit(values);

    if (!valid) {
      showTabWithError();
      return;
    }

    onClose();
  };

  const handleSubmitAndMore = async () => {
    await onSubmit(values);

    if (!valid) {
      showTabWithError();
      return;
    }

    restart();
  };

  const showTabWithError = () => {
    const erroredField = Object.keys(errors)[0];
    const fieldsInTabs = Object.entries(fieldsByTabs);

    for (let i = 0; i < fieldsInTabs.length; i++) {
      if (fieldsInTabs[i][1].includes(erroredField)) {
        setSelectedTab(Number(fieldsInTabs[i][0]));

        const fieldState = getFieldState(erroredField);
        fieldState.blur();
      }
    }
  };

  const validateField = (fieldName) => {
    const fieldError = errors[fieldName];

    if (fieldError) {
      const fieldState = getFieldState(fieldName);
      fieldState.blur();
    }

    return Boolean(fieldError);
  };

  const handleChangeTheTab = (index) => {
    const requiredFields = requiredFieldsForTabs[index];

    const errorFields = requiredFields.filter((requiredField) => validateField(requiredField));

    if (!errorFields.length) {
      setSelectedTab(index);
    }
  };

  return (
    <>
      <div className={scss.title}>{isEdit ? t('editDataset') : t('addDataset')}</div>

      <Tabs className={scss.tabs} onSelect={handleChangeTheTab} selectedIndex={selectedTab} forceRenderTabPanel>
        <TabList className={scss.tabList}>
          <Tab>{t('general')}</Tab>
          <Tab>{t('projectSettings')}</Tab>
          <Tab>{t('social')}</Tab>
        </TabList>

        <TabPanel className={scss.tabPanel} tabIndex={SONAR_DATASET_TABS.GENERAL}>
          <GeneralSection />
        </TabPanel>

        <TabPanel className={scss.tabPanel} tabIndex={SONAR_DATASET_TABS.PROJECT_SETTINGS}>
          <ProjectSettingsSection />
        </TabPanel>

        <TabPanel className={scss.tabPanel} tabIndex={SONAR_DATASET_TABS.SOCIAL}>
          <SocialSection />
        </TabPanel>
      </Tabs>

      <div className={scss.actions}>
        <div className={scss.leftActions}>
          {isEdit ? (
            <CommonButton
              styleColor='primary'
              type='submit'
              maxWidth={150}
              onClick={handleSubmitAndCLose}
              isLoading={isLoading}>
              {t('edit')}
            </CommonButton>
          ) : (
            <>
              <CommonButton
                styleColor='primary'
                type='submit'
                maxWidth={180}
                onClick={handleSubmitAndMore}
                isLoading={isLoading}>
                {t('saveAndAddMore')}
              </CommonButton>
              <CommonButton
                styleColor='secondary'
                type='submit'
                maxWidth={180}
                onClick={handleSubmitAndCLose}
                isLoading={isLoading}>
                {t('saveAndClose')}
              </CommonButton>
            </>
          )}
        </div>

        <CommonButton styleColor='secondary' maxWidth={100} onClick={onClose}>
          {t('cancel')}
        </CommonButton>
      </div>
    </>
  );
};

export default SonarDatasetModalForm;
